// useRouteMeta.js
// Automatically applies meta tags based on current route
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getMetaForRoute, getDynamicMeta } from "../config/metaConfig";

export default function useRouteMeta(customMeta = {}, dynamicParams = {}) {
  const location = useLocation();
  
  useEffect(() => {
    // Get meta data for current route
    const routeMeta = getMetaForRoute(location.pathname);
    
    // Apply dynamic parameters if any
    const finalMeta = getDynamicMeta(location.pathname, dynamicParams);
    
    // Merge with custom meta (custom meta takes precedence)
    const mergedMeta = { ...finalMeta, ...customMeta };
    
    // Update title
    if (mergedMeta.title) {
      document.title = mergedMeta.title;
    }

    // Update description meta tag
    if (mergedMeta.description) {
      let meta = document.querySelector("meta[name='description']");
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = mergedMeta.description;
    }

    // Update keywords meta tag
    if (mergedMeta.keywords) {
      let meta = document.querySelector("meta[name='keywords']");
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "keywords";
        document.head.appendChild(meta);
      }
      meta.content = mergedMeta.keywords;
    }

    // Update Open Graph meta tags
    const ogTags = [
      { property: 'og:title', content: mergedMeta.ogTitle },
      { property: 'og:description', content: mergedMeta.ogDescription },
      { property: 'og:image', content: mergedMeta.ogImage },
      { property: 'og:url', content: mergedMeta.ogUrl },
      { property: 'og:type', content: mergedMeta.ogType || 'website' },
      { property: 'og:site_name', content: 'Glammify' }
    ];

    ogTags.forEach(tag => {
      if (tag.content) {
        let meta = document.querySelector(`meta[property="${tag.property}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("property", tag.property);
          document.head.appendChild(meta);
        }
        meta.content = tag.content;
      }
    });

    // Update Twitter Card meta tags
    const twitterTags = [
      { name: 'twitter:card', content: mergedMeta.twitterCard },
      { name: 'twitter:title', content: mergedMeta.twitterTitle },
      { name: 'twitter:description', content: mergedMeta.twitterDescription },
      { name: 'twitter:image', content: mergedMeta.twitterImage }
    ];

    twitterTags.forEach(tag => {
      if (tag.content) {
        let meta = document.querySelector(`meta[name="${tag.name}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = tag.name;
          document.head.appendChild(meta);
        }
        meta.content = tag.content;
      }
    });

    // Cleanup function
    return () => {
      // You can add cleanup logic here if needed
    };
  }, [location.pathname, customMeta, dynamicParams]);
}
